import React from 'react'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useScanNetworkDialog } from 'src/Libs/installations/hooks/installation-configuration/storage/use-scan-network-dialog'
import ScanResultsList from './ScanResultsList'
import { BlankView } from 'src/Components/blank-view'

export interface ScanNetworkDialogProps {
  onClose?: () => void
  emcSerialnumber: string
  installationId: number
  emcDeviceLinkId: number
}

const ScanNetworkDialog: React.FC<ScanNetworkDialogProps> = ({ emcSerialnumber, installationId, emcDeviceLinkId, onClose }) => {
  const { t } = useCustomTranslation()
  const { serialList, error, hasScanError, isSaving, isSynchronizing, isScanning, hasScanResults, handleCancel, handleRetryScan, handleSave } =
    useScanNetworkDialog({
      onClose,
      emcSerialnumber,
      installationId,
      emcDeviceLinkId,
    })

  const primaryAction = hasScanError
    ? {
        name: t('installation-configuration.storage.scan-network-dialog.retry'),
        onClick: handleRetryScan,
        disabled: isSaving || isSynchronizing,
      }
    : {
        name: t('common.save'),
        onClick: handleSave,
        disabled: isScanning || !hasScanResults || isSaving || isSynchronizing || serialList.length === 0,
      }

  const renderDialogContent = (): React.ReactNode => {
    if (hasScanError) {
      return (
        <ScanErrorContent
          error={error}
          isSaving={isSaving}
          isSynchronizing={isSynchronizing}
          retryLabel={t('installation-configuration.storage.scan-network-dialog.retry')}
          closeLabel={t('common.close')}
          commonErrorLabel={t('common.error')}
          onRetry={handleRetryScan}
          onClose={handleCancel}
        />
      )
    }

    if (isScanning) {
      return <ScanLoadingContent scanningLabel={t('installation-configuration.storage.scan.dialog.scanning')} />
    }

    if (hasScanResults && serialList.length === 0) {
      return <ScanNoResultsContent noResultsLabel={t('installation-configuration.storage.scan.dialog.no-results')} />
    }

    if (hasScanResults) {
      return <ScanResultsList serialNumbers={serialList} />
    }

    return null
  }

  return (
    <Dialog
      onClose={handleCancel}
      maxWidth="sm"
      title={t('installation-configuration.storage.scan.dialog.title')}
      actions={{
        primary: primaryAction,
        secondary: {
          name: t('common.cancel'),
          onClick: handleCancel,
          disabled: isSaving || isSynchronizing,
        },
      }}
    >
      <Stack sx={{ py: 2 }}>{renderDialogContent()}</Stack>
    </Dialog>
  )
}

// #region ScanErrorContent
interface ScanErrorContentProps {
  error: unknown
  isSaving: boolean
  isSynchronizing: boolean
  retryLabel: string
  closeLabel: string
  commonErrorLabel: string
  onRetry: () => void
  onClose: () => void
}

const ScanErrorContent: React.FC<ScanErrorContentProps> = () => {
  return <BlankView imageSize={'SMALL'} title={'installation-configuration.storage.scan.dialog.error'} imageVariant={'WORRIED'} />
}
// #endregion

// #region ScanLoadingContent
interface ScanLoadingContentProps {
  scanningLabel: string
}

const ScanLoadingContent: React.FC<ScanLoadingContentProps> = ({ scanningLabel }) => {
  return (
    <Stack alignItems="center" justifyContent="center" gap={2} py={4}>
      <CircularProgress />
      <Typography variant="body2">{scanningLabel}</Typography>
    </Stack>
  )
}
// #endregion

// #region ScanNoResultsContent
interface ScanNoResultsContentProps {
  noResultsLabel: string
}

const ScanNoResultsContent: React.FC<ScanNoResultsContentProps> = ({ noResultsLabel }) => {
  return <BlankView imageSize={'SMALL'} title={noResultsLabel} imageVariant={'NEUTRAL'} />
}
// #endregion

export default ScanNetworkDialog
